import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    TextField
} from "@mui/material";
import type {
    CreateIntegrationRequest,
    EmbedAuthMode,
    EmbedProviderType
} from "../../../../common/interfaces/admin/integration.interface";

/**
 * Wizard Step "Authentication": pick how visitors are identified.
 *
 *  - anonymous      → no sign-in (Phase 1 behaviour; the default).
 *  - sso_redirect   → federated SSO via the customer's SAML/OIDC IdP. Requires
 *                     a provider type + a display name now; the IdP details and
 *                     test happen on the integration's SSO tab after creation
 *                     (the Firebase provider needs the created integration id).
 *  - signed_identity→ widget-only host-signed hand-off. Enabled only when the
 *                     widget surface is on; the HMAC/JWT config is entered on
 *                     the Signed Identity tab after creation.
 */
export interface IntegrationAuthStepProps {
    value: Pick<
        CreateIntegrationRequest,
        "authMode" | "providerType" | "providerDisplayName" | "widgetEnabled"
    >;
    onChange(patch: Partial<IntegrationAuthStepProps["value"]>): void;
}

export default function IntegrationAuthStep({ value, onChange }: IntegrationAuthStepProps) {
    const authMode: EmbedAuthMode = value.authMode ?? "anonymous";
    const isSso = authMode === "sso_redirect";
    const isSigned = authMode === "signed_identity";
    const widgetEnabled = !!value.widgetEnabled;

    return (
        <div className="integration-step">
            <FormControl>
                <FormLabel>Authentication mode</FormLabel>
                <RadioGroup
                    value={authMode}
                    onChange={e => {
                        const next = e.target.value as EmbedAuthMode;
                        // Clear SSO-only fields when leaving sso_redirect so we
                        // never POST a stale providerType/name on a non-SSO row.
                        onChange(
                            next === "sso_redirect"
                                ? { authMode: next, providerType: value.providerType ?? "saml" }
                                : {
                                      authMode: next,
                                      providerType: undefined,
                                      providerDisplayName: undefined
                                  }
                        );
                    }}
                >
                    <FormControlLabel
                        value="anonymous"
                        control={<Radio />}
                        label="Anonymous — no sign-in"
                    />
                    <FormControlLabel
                        value="sso_redirect"
                        control={<Radio />}
                        label="Single sign-on (SAML / OIDC via the customer's IdP)"
                    />
                    <FormControlLabel
                        value="signed_identity"
                        control={<Radio />}
                        disabled={!widgetEnabled}
                        label="Signed identity (host-signed hand-off) — widget only"
                    />
                </RadioGroup>
                {!widgetEnabled && (
                    <FormHelperText>
                        Enable the widget surface (Basics step) to use signed identity.
                    </FormHelperText>
                )}
            </FormControl>

            {isSigned && (
                <FormHelperText>
                    After creating, configure the HMAC secret or JWT settings on the Signed Identity
                    tab. Visitors are identified by a signed hand-off from your backend — there is
                    no sign-in button.
                </FormHelperText>
            )}

            {isSso && (
                <>
                    <FormControl>
                        <FormLabel>Provider type</FormLabel>
                        <RadioGroup
                            row
                            value={value.providerType ?? "saml"}
                            onChange={e =>
                                onChange({ providerType: e.target.value as EmbedProviderType })
                            }
                        >
                            <FormControlLabel value="saml" control={<Radio />} label="SAML" />
                            <FormControlLabel value="oidc" control={<Radio />} label="OIDC" />
                        </RadioGroup>
                        <FormHelperText>
                            The IdP details (SSO URL, certificate / client secret) are entered on
                            the SSO tab after this integration is created, then tested before it can
                            be activated.
                        </FormHelperText>
                    </FormControl>

                    <TextField
                        label="Sign-in button label"
                        value={value.providerDisplayName ?? ""}
                        onChange={e => onChange({ providerDisplayName: e.target.value })}
                        placeholder="e.g. Acme SSO"
                        helperText="Shown to visitors on the sign-in button."
                        fullWidth
                    />
                </>
            )}
        </div>
    );
}
